"use client";
import parse from "html-react-parser";
import { useCTANavigation } from "@/hooks/use-cta-navigation";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import { EventNames, sendEvent } from "@/lib/utils/analytics";
import { useApp } from "./providers/app-provider";
import { useCheckout } from "@/hooks/use-checkout";
import { SALE_CTA_DISCOUNT_AMOUNT } from "@/lib/utils/common/constants";
import { isSaleEnabled } from "@/lib/config/sale";

type CTAType = "starter_kit" | "pitch" | "buy_now" | "checkout";
interface CTAButtonProps {
  text: string;
  caption?: string;
  className?: string;
  textClassName?: string;
  captionClassName?: string;
  variant?:
    | "default"
    | "outline"
    | "ghost"
    | "link"
    | "destructive"
    | "secondary"
    | "destructive";
  type?: CTAType;
  testId: string;
  handleCTAClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  containerClassName?: string;
  disabled?: boolean;
  eventName?: keyof typeof EventNames | string;
  eventProperties?: Record<string, unknown>;
}

export function CTAButton({
  text,
  caption,
  className,
  textClassName,
  captionClassName,
  variant,
  type,
  testId,
  handleCTAClick,
  containerClassName,
  eventName,
  eventProperties,
  ...buttonProps
}: CTAButtonProps) {
  const { actions } = useCTANavigation();
  const { handleCheckout, isRedirecting } = useCheckout();

  // Safer option adding fallback mapping if eventName is not provided
  const defaultEventByType: Partial<Record<CTAType, string>> = {
    pitch: EventNames.HOW_INITO_WORK_CLICKED,
    buy_now: EventNames.GET_INITO_KIT_CLICKED,
  };

  const { region, workflow } = useApp();

  const isSale = isSaleEnabled("ctaButton");
  const saleAmount = SALE_CTA_DISCOUNT_AMOUNT[workflow || "og"];
  const displayCaption =
    caption && isSale
      ? caption.replace(/\$\d+/g, `$${saleAmount}`)
      : caption;

  return (
    <div
      className={cn("flex flex-col gap-4 md:max-w-[350px]", containerClassName)}
      id={`cta-button-${testId}-container`}
      data-testid={`cta-button-${testId}-container`}
    >
      <Button
        className={cn(
          "h-auto rounded-lg py-3 md:w-[204px] md:py-4 hover:cursor-pointer",
          className
        )}
        variant={variant}
        onClick={(e) => {
          const finalEventName =
            eventName ??
            (type && type !== "checkout" ? defaultEventByType[type] : undefined);
          // Checkout fires checkout_now_clicked* from useCheckout (intermediate vs shopify)
          if (finalEventName && type !== "checkout") {
            // If a key from EventNames was passed, resolve it to the value
            const resolvedName =
              typeof finalEventName === "string"
                ? finalEventName
                : EventNames[finalEventName];
            sendEvent(resolvedName, eventProperties);
          }

          // 2) Run navigation action or checkout
          if (type === "checkout") {
            handleCheckout();
          } else if (type) {
            actions[type]?.(e);
          }

          // 3) Let callers run custom logic
          handleCTAClick?.(e);
        }}
        data-testid={testId}
        id={testId}
        disabled={
          type === "checkout" && (region === "default" || isRedirecting)
        }
        {...buttonProps}
      >
        <span
          className={cn(
            `text-base font-medium leading-6 tracking-[-0.4px] md:text-[17px] md:leading-[26px]`,
            textClassName
          )}
          id={`cta-button-${testId}-text`}
          data-testid={`cta-button-${testId}-text`}
        >
          {type === "checkout" && isRedirecting ? (
            <span className="flex items-center gap-2">
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="hidden md:inline">Preparing</span>
              <span className="md:hidden">Preparing your cart...</span>
            </span>
          ) : (
            text
          )}
        </span>
      </Button>
      {displayCaption && (
        <p
          className={cn(
            "self-center text-sm font-medium leading-4 text-secondary md:self-start md:text-[17px]",
            captionClassName
          )}
          id={`cta-button-${testId}-caption`}
          data-testid={`cta-button-${testId}-caption`}
        >
          {parse(displayCaption)}
        </p>
      )}
    </div>
  );
}
